import { HttpStatus } from '@monorepo/client-types';
import { Application } from '@monorepo/types';
import { getProject } from '@hub/helpers/get-project';
import * as Sentry from '@sentry/react';
import { EnvService } from './env-service';

const isProduction = EnvService.isProduction;

export enum ERROR_TYPES {
  API = 'API Error',
  CLIENT = 'Client Error',
  UNEXPECTED = 'Unexpected Error',
}

export enum ERROR_MESSAGES {
  DEFAULT = 'An error occurred',
  NO_RESPONSE = 'No response received from server',
}

type TError = {
  name?: string;
  message?: string;
  status?: HttpStatus;
  data?: any;
};

/**
 * Represents an API error with additional context and error handling.
 *
 * This class extends the standard Error object to include an HTTP status code
 * and optional data payload. In production environments, it utilizes the
 * SentryService to capture and log the error for monitoring purposes.
 * @param name - The error name.
 * @param project - The project name.
 * @param message - The error message.
 * @param status - The HTTP status code associated with the error.
 * @param data - Optional additional data related to the error.
 */
export class UIErrorService extends Error {
  public name: string;
  public project: Application;
  public status: HttpStatus;
  public data: any;

  constructor({
    name = ERROR_TYPES.API,
    message = ERROR_MESSAGES.DEFAULT,
    status = HttpStatus.INTERNAL_SERVER_ERROR,
    data = null,
  }: TError) {
    super(message);

    this.project = getProject().project;
    this.name = name;
    this.status = status;
    this.data = data;

    this.handleError();
  }

  private handleError() {
    isProduction
      ? this.handleProductionErrors()
      : this.handleDevelopmentErrors();
  }

  private handleProductionErrors() {
    if (
      [!HttpStatus.FORBIDDEN, HttpStatus.UNAUTHORIZED].includes(this.status)
    ) {
      Sentry.captureMessage(this.name, 'error');
      Sentry.captureException(JSON.stringify(this));
    }
  }

  private handleDevelopmentErrors() {
    console.error(this.name, { ...this });
  }
}
